import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';

export default function WorldCup() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'FIFA World Cup Technology Partnership',
        item: `${siteUrl}/world-cup`
      }
    ]
  };

  const worldCupSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'FIFA World Cup Technology Partner | Diivy',
    url: `${siteUrl}/world-cup`,
    description: 'Diivy served as the official Technology Partner for FIFA World Cup, delivering comprehensive RFID and access control infrastructure managing millions of credentials across multiple venues and thousands of access points.',
    about: {
      '@type': 'Event',
      name: 'FIFA World Cup Qatar 2022',
      description: 'World-class RFID and access control infrastructure for the FIFA World Cup',
      organizer: {
        '@type': 'Organization',
        name: 'FIFA'
      }
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'Diivy Medical Technology',
      url: siteUrl,
      description: 'Official Technology Partner for FIFA World Cup, delivering RFID and access control infrastructure'
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="FIFA World Cup Technology Partner | RFID & Access Control | Diivy"
        description="Diivy served as the official Technology Partner for FIFA World Cup, delivering comprehensive RFID and access control infrastructure managing 5M+ credentials across 8 venues and 3,000+ access points."
        keywords="FIFA World Cup, Technology Partner, RFID infrastructure, access control, credential management, sports technology, Qatar 2022"
        canonical="/world-cup"
        ogTitle="FIFA World Cup Technology Partner | Diivy"
        ogDescription="Delivering world-class RFID and access control infrastructure for the FIFA World Cup — 5M+ credentials, 8 venues, 3,000+ access points."
        ogUrl="/world-cup"
        twitterTitle="FIFA World Cup Technology Partner | Diivy"
        twitterDescription="Delivering world-class RFID and access control infrastructure for the FIFA World Cup."
      />
      <JsonLd data={worldCupSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-[88px] min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]">
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-96 h-96 bg-[#C9A96E] rounded-full blur-[120px]"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#C9A96E] rounded-full blur-[120px]"></div>
          </div>

          <div className="relative z-10 max-w-[1400px] mx-auto px-12 py-24 w-full flex flex-col gap-16">
            {/* Top row: Text left, Video right */}
            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Left: Text content */}
              <div className="flex-1 text-left">
                <div className="inline-block px-5 py-2 bg-[#C9A96E]/10 border border-[#C9A96E]/30 rounded-full mb-8">
                  <span className="text-[13px] font-medium text-[#C9A96E] tracking-wide">OFFICIAL TECHNOLOGY PARTNER</span>
                </div>

                <h1 className="text-6xl md:text-7xl font-bold text-white mb-8 leading-tight">
                  FIFA World Cup<br />
                  <span className="text-[#C9A96E]">Technology Partnership</span>
                </h1>

                <p className="text-xl text-gray-300 max-w-xl leading-relaxed">
                  Delivering world-class RFID and access control infrastructure for the world's most prestigious sporting event
                </p>
              </div>

              {/* Right: Video */}
              <div className="flex-1 w-full">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src="https://player.vimeo.com/video/1173492839?badge=0&autopause=0&player_id=0&app_id=58479"
                    className="absolute top-0 left-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    title="FIFA World Cup Technology"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Bottom row: Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
                <div className="text-5xl font-bold text-[#C9A96E] mb-3">5M+</div>
                <div className="text-sm text-gray-300 tracking-wide">Credentials Managed</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
                <div className="text-5xl font-bold text-[#C9A96E] mb-3">8</div>
                <div className="text-sm text-gray-300 tracking-wide">Stadium Venues</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
                <div className="text-5xl font-bold text-[#C9A96E] mb-3">3,000+</div>
                <div className="text-sm text-gray-300 tracking-wide">Access Points</div>
              </div>
            </div>
          </div>
        </section>

        {/* Role & Responsibilities */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-12">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our Role as <span className="text-[#C9A96E]">Technology Partner</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Comprehensive RFID and access control solutions ensuring security, efficiency, and seamless operations throughout the tournament
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-3xl p-10 hover:shadow-xl transition-shadow duration-400">
                <div className="w-16 h-16 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-3xl text-[#C9A96E]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Access Control Infrastructure</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Deployed and managed over 3,000 RFID access points across 8 stadium venues, ensuring secure and efficient entry for players, officials, staff, and VIPs.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Real-time credential verification</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Multi-zone access management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Automated security protocols</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-3xl p-10 hover:shadow-xl transition-shadow duration-400">
                <div className="w-16 h-16 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-bank-card-line text-3xl text-[#C9A96E]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Credential Management System</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Managed over 5 million RFID credentials for diverse stakeholder groups, from athletes and media to volunteers and contractors.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Centralized credential issuance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Role-based access permissions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Instant activation and deactivation</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-3xl p-10 hover:shadow-xl transition-shadow duration-400">
                <div className="w-16 h-16 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-line-chart-line text-3xl text-[#C9A96E]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Real-Time Monitoring & Analytics</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Provided tournament organizers with comprehensive dashboards tracking access patterns, capacity management, and security alerts.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Live occupancy tracking</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Incident response coordination</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Post-event analytics reporting</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-3xl p-10 hover:shadow-xl transition-shadow duration-400">
                <div className="w-16 h-16 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-[#C9A96E]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Technical Support</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Maintained round-the-clock technical support teams at all venues to ensure zero downtime and immediate issue resolution.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">On-site technical teams</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Remote monitoring center</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C9A96E] text-xl mt-0.5"></i>
                    <span className="text-gray-700">Rapid response protocols</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Credential Badge Showcase */}
        <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-[1400px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block px-5 py-2 bg-[#C9A96E]/10 border border-[#C9A96E]/30 rounded-full mb-6">
                  <span className="text-[13px] font-medium text-[#C9A96E] tracking-wide">CREDENTIAL TECHNOLOGY</span>
                </div>
                <h2 className="text-5xl font-bold text-gray-900 mb-6">
                  Advanced RFID<br />
                  <span className="text-[#C9A96E]">Credential System</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Each credential badge featured cutting-edge RFID technology, enabling instant authentication, zone-specific access control, and comprehensive tracking throughout the tournament.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#C9A96E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-flashlight-line text-xl text-[#C9A96E]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Instant Read Technology</h4>
                      <p className="text-gray-600 text-sm">Sub-second authentication for seamless entry</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#C9A96E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-2-line text-xl text-[#C9A96E]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Encrypted Data Protection</h4>
                      <p className="text-gray-600 text-sm">Military-grade security protocols</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#C9A96E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-[#C9A96E]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Zone-Based Access</h4>
                      <p className="text-gray-600 text-sm">Granular control across all venue areas</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="relative z-10 bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/0513175d911671da7753552caa92d476.jpg"
                    alt="FIFA World Cup RFID Credential Badge"
                    className="w-full h-auto object-contain rounded-2xl"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-[#C9A96E]/20 rounded-full blur-[100px]"></div>
                <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#C9A96E]/10 rounded-full blur-[100px]"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Tournament <span className="text-[#C9A96E]">Achievements</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Delivering flawless performance under the world's spotlight
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-time-line text-4xl text-[#C9A96E]"></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">99.99%</div>
                <div className="text-sm text-gray-600 tracking-wide">System Uptime</div>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-speed-line text-4xl text-[#C9A96E]"></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">&lt;0.5s</div>
                <div className="text-sm text-gray-600 tracking-wide">Average Access Time</div>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-check-line text-4xl text-[#C9A96E]"></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">Zero</div>
                <div className="text-sm text-gray-600 tracking-wide">Security Breaches</div>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-[#C9A96E]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-team-line text-4xl text-[#C9A96E]"></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">200+</div>
                <div className="text-sm text-gray-600 tracking-wide">Technical Staff</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-[#C9A96E] rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C9A96E] rounded-full blur-[120px]"></div>
          </div>

          <div className="relative z-10 max-w-[1400px] mx-auto px-12 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Ready to Deploy World-Class<br />
              <span className="text-[#C9A96E]">RFID Solutions?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
              Leverage our proven expertise in large-scale event technology infrastructure
            </p>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-[#C9A96E] text-white text-base font-medium rounded-full hover:bg-[#B8956A] transition-all duration-400 whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              Contact Our Team
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}